import { Icon } from "@mdi/react";
import { FC, memo, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  mdiAlertCircle,
  mdiDelete,
  mdiFolderOpen,
  mdiPencil,
  mdiPlus,
  mdiRefresh,
  mdiStar,
  mdiStarOutline
} from "@mdi/js";
import {
  Button,
  Card,
  Collapse,
  List,
  Modal,
  Popconfirm,
  Popover,
  Space,
  Tooltip
} from "antd";
import { DEFAULT_NORMALIZE_BY } from "constants/chart.constants";
import { DEFAULT_TARGET, ErrorTypes } from "constants/ml.constants";
import {
  clearError,
  clearSummary,
  setInputFeatures,
  setMLNormalizationFeatureSetting,
  setMLNormalizationFeatures,
  setTargetFeature
} from "store/features/ml/mlSlice";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import formatPropertyName from "utils/column/formatPropertyName";
import getColumnLabelWithContext from "utils/column/getColumnLabelWithContext";
import getGroupByListColumnByProperty from "utils/column/getGroupByListColumnByProperty";
import getProductDisplayName from "utils/data-table/getProductDisplayName";

import { useEvaUnitCore, useUserUnit } from "hooks";

import GroupBy from "components/groupBy";

import { StoredFeatureTemplate } from "../../api/featureTemplate";
import { useFeatureTemplates } from "../../hooks/useFeatureTemplates";
import useUserModules from "../../hooks/useUserModules";
import { DataEnum, IGroupBy } from "../../models";
import { INormalizeBySetting } from "../../models/normalizeBy";
import { IconButton } from "../forecasting/Shared";
import InlineTemplateRename from "./InlineTemplateRename";
import SaveTemplateButton from "./SaveTemplateButton";
import TemplateFeaturesTable from "./TemplateFeaturesTable";
import { getDefaultFeaturesWithUnits, getDefaultTemplate } from "./defaultTemplate";
import { NormalizeByToggle } from "./normalize";
import {
  FeatureImportanceSummary,
  MlFeature,
  MlTarget,
  NormalizeFeaturePackage
} from "./types";
import { getNormalizedByLabel } from "./utils/nameUtils";

interface FeatureSelectorProps {
  setErrorModalVisible?: (visible: boolean) => void;
}

const RootContainer = styled.div`
  width: 100%;
  height: 100%;
  max-height: 100%;
  overflow: hidden;

  .ant-list-split {
    padding: 0;
  }

  .ant-list-action {
    margin-left: 0;
  }
`;

const StyledCard = styled(Card)`
  height: 100%;

  .ant-card-body {
    height: 100%;
    display: grid;
    grid-template-rows: auto auto 1fr; // first row auto accounts for ant-card ::before
    padding: 0 !important; //override default padding for better scroll bar placement
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 10px 10px 12px; //extra padding added for scrollbar space
`;

const FeatureSelectorRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 0;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  .ant-space {
    margin-bottom: 0 !important;
    flex-grow: 1;

    .ant-space-item {
      display: flex;
      flex-grow: 1;
    }

    .ant-space-item .ant-btn,
    .ant-space-item span {
      flex-grow: 1;
      width: 100%;
    }
  }
`;

const StyledAddFeatureButton = styled(Button)`
  width: 100%;
  min-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const ScrollableList = styled(List)`
  overflow-y: auto;
  padding: 0 10px 10px !important; //extra padding added for scrollbar space
`;

const ListItemWrapper = styled(List.Item)`
  display: flex;
  flex-direction: column;
  padding: 3px;

  .ant-list-item-action {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    width: 100%;
    margin: 0;

    & > li {
      padding: 0 8px;

      button {
        width: 100%;
        display: flex;
        height: 26px;
        align-items: center;
        justify-content: center;
        padding: 0;
      }
    }
  }

  .featureSelectorDeleteButton {
    color: #a2aaad;

    &:hover {
      color: #f5222d;
    }
  }

  @media screen and (max-width: 1600px) {
    .ant-list-item-action > li {
      padding: 0 4px;
    }
  }
`;

const FeatureRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  min-height: 32px;
  height: auto;
`;

const FeatureNameColumn = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const NormalizedByLabel = styled.span`
  font-size: 12px;
  color: var(--color-text-60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  padding-bottom: 8px;
`;

const MonotonicPopoverContent = styled.div`
  min-width: 120px;

  & > div:first-child {
    font-weight: 600;
    margin-bottom: 8px;
    text-align: left;
  }

  .ant-space {
    width: 100%;
    justify-content: center;
  }

  .ant-btn {
    display: flex;
    gap: 5px;
  }
`;

const MonotonicityButtonContent = styled.span<{ color?: string }>`
  font-size: 18px;
  font-weight: bold;
  color: ${(props) => props.color || "#bfbfbf"};
  line-height: 1;
`;

const FeatureName = styled.span`
  display: inline-block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
`;

const TemplateModal = styled(Modal)`
  .ant-modal-content {
    overflow: hidden;
  }

  .ant-modal-body {
    max-height: 60vh;
    overflow-y: auto;
    padding: 8px 16px;
  }
`;

const TemplateListItem = styled(List.Item)<{ $isActive?: boolean }>`
  transition: all 0.2s ease;
  background-color: ${(props) => (props.$isActive ? "#e6fffb" : "transparent")};
  border-left: ${(props) =>
    props.$isActive ? "3px solid var(--color-primary)" : "3px solid transparent"};
  padding: 12px 16px 12px ${(props) => (props.$isActive ? "12px" : "16px")} !important;
  margin-bottom: 0;
  cursor: pointer;

  &:hover {
    background-color: ${(props) =>
      props.$isActive ? "#e6fffb" : "var(--color-surface-02)"};
  }

  .ant-list-item-meta {
    padding-left: 0 !important;
  }
`;

const TemplateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 8px;

  .ant-btn-dangerous {
    color: #a2aaad;

    &:hover {
      color: #f5222d;
    }
  }
`;

const FavoriteIconButton = styled.button<{ $isFavorite: boolean }>`
  background-color: ${(props) =>
    props.$isFavorite ? "var(--color-primary)" : "transparent"};
  border: 1px solid
    ${(props) => (props.$isFavorite ? "var(--color-primary)" : "transparent")};
  border-radius: 4px;
  width: 24px;
  height: 24px;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: ${(props) => (props.$isFavorite ? "#ffffff" : "#a2aaad")};
  }

  &:hover {
    background-color: ${(props) =>
      props.$isFavorite ? "var(--color-primary)" : "rgba(0, 0, 0, 0.02)"};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    svg {
      color: ${(props) => (props.$isFavorite ? "#ffffff" : "var(--color-primary)")};
    }
  }
`;

const TemplateName = styled.div`
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
`;

const TemplateInfo = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`;

const TargetInfoContainer = styled.div`
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
`;

const TargetInfoLabel = styled.div`
  font-weight: 500;
  color: var(--color-text);
`;

const TargetNormLabel = styled.div`
  color: var(--color-text);
`;

const CollapseContainer = styled.div`
  .ant-collapse {
    .ant-collapse-header {
      padding: 8px 16px;

      &:hover {
        .ant-collapse-expand-icon {
          .collapseChevron::after {
            border-color: var(--color-primary);
          }
        }
      }
    }

    .collapseChevron {
      transition: all 0.2s;
      transform: rotate(270deg);
      vertical-align: 0 !important;

      &::after {
        content: "";
        display: block;
        width: 7px;
        height: 7px;
        border: 1px solid var(--color-text);
        border-width: 0 1px 1px 0;
        transform: rotate(45deg);
      }

      &.active {
        transform: rotate(360deg);
      }
    }

    .ant-collapse-content-box {
      padding: 0 16px 8px;
    }
  }
`;

// Component to handle tooltip with truncation detection
const TemplateNameWithTooltip: FC<{ name: string }> = ({ name }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = () => {
    if (ref.current) {
      setIsTruncated(ref.current.scrollWidth > ref.current.clientWidth);
    }
  };

  return (
    <Tooltip title={isTruncated ? name : ""} placement="top">
      <TemplateName ref={ref} onMouseEnter={checkTruncation}>
        {name}
      </TemplateName>
    </Tooltip>
  );
};

const FeatureSelector: FC<FeatureSelectorProps> = ({ setErrorModalVisible }) => {
  const dispatch = useDispatch();
  const categoryList = useSelector((state: RootState) => state.groupBy.categoryList);
  const { has3dGeoModel } = useUserModules();

  const defaultFeatures = useMemo(
    () => getDefaultFeaturesWithUnits(categoryList, has3dGeoModel),
    [categoryList, has3dGeoModel]
  );
  const storedFeatures = useSelector(
    (state: RootState) => state.ml.inputFeatures
  ) as MlFeature[];
  const [featureList, setFeatureList] = useState<MlFeature[]>(storedFeatures ?? []);
  const hasInitializedFeatures = useRef(!!storedFeatures?.length);
  const { templates, isLoading, saveTemplates } = useFeatureTemplates();
  const [monotonicPopover, setMonotonicPopover] = useState<string | null>(null);
  const [loadModalVisible, setLoadModalVisible] = useState(false);
  const [currentTemplateName, setCurrentTemplateName] = useState<string | null>(null);
  const [editingTemplateIndex, setEditingTemplateIndex] = useState<number | null>(null);
  const [favoriteTemplateIndex, setFavoriteTemplateIndex] = useState<number | null>(null);
  const mlState = useSelector((state: RootState) => state.ml);
  const [featuresWithError, setFeaturesWithError] = useState<string[] | null>(null);
  const { isReady: isUnitCoreReady, getTargetUnit } = useEvaUnitCore();
  const unitType = useUserUnit();

  const DEFAULT_TEMPLATE = useMemo(
    () => getDefaultTemplate(categoryList, has3dGeoModel),
    [categoryList, has3dGeoModel]
  );

  const featuresWithAppliedNormalization: NormalizeFeaturePackage[] =
    (mlState.featureImportanceSummary as FeatureImportanceSummary)?.well_tracking_data
      ?.normalizationSummary ?? [];
  const getDisplayUnit = (sourceUnit: Maybe<string>): string => {
    if (!sourceUnit) return "";
    const srcUnit = sourceUnit.replace(/[()]/g, "");
    if (!srcUnit || !isUnitCoreReady) return sourceUnit;
    const targetUnit = getTargetUnit(srcUnit, unitType);
    return `(${targetUnit})`;
  };

  // Initialize features on ML tab load - prioritizes starred template, falls back to defaults
  useEffect(() => {
    if (categoryList.length > 0 && !hasInitializedFeatures.current) {
      // Wait for templates to finish loading before initializing
      if (isLoading) return;

      let featuresToLoad = defaultFeatures;
      let templateName = DEFAULT_TEMPLATE.name;
      let favoriteIndex: number | null = null;

      // Check if any saved templates exist and have a starred (isDefault) template
      if (templates.length > 0) {
        const starredTemplate = templates.find((t) => t.isDefault === true);

        if (starredTemplate) {
          // Load the starred template
          featuresToLoad = starredTemplate.features;
          templateName = starredTemplate.name;
          favoriteIndex = templates.indexOf(starredTemplate);
        }
        // Note: if templates exist but none are starred, still use defaults (don't load unstarred templates)
      }

      // Apply the resolved features and state
      if (featuresToLoad.length > 0) {
        setFeatureList(featuresToLoad);
        dispatch(setInputFeatures(featuresToLoad));

        // Restore normalization and target settings if loading a starred template
        if (templates.find((t) => t.isDefault === true)) {
          const starredTemplate = templates.find((t) => t.isDefault === true);
          if (starredTemplate) {
            dispatch(
              setMLNormalizationFeatures(starredTemplate.normalizationFeatures ?? [])
            );
            if (starredTemplate.targetFeature) {
              dispatch(setTargetFeature(starredTemplate.targetFeature));
            } else {
              dispatch(setTargetFeature(DEFAULT_TARGET));
            }
          }
        } else {
          // Reset to defaults if no starred template
          dispatch(setMLNormalizationFeatures([]));
          dispatch(setTargetFeature(DEFAULT_TARGET));
        }

        setCurrentTemplateName(templateName);
        if (favoriteIndex !== null) {
          setFavoriteTemplateIndex(favoriteIndex);
        }
        hasInitializedFeatures.current = true;
      }
    }
  }, [categoryList, defaultFeatures, templates, dispatch, isLoading]);

  useEffect(() => {
    if (storedFeatures?.length > 0) {
      setFeatureList(storedFeatures);
    }
  }, [storedFeatures]);

  // Scroll to selected template when modal opens
  useEffect(() => {
    if (loadModalVisible && currentTemplateName) {
      setTimeout(() => {
        const activeElement = document.querySelector('[data-template-active="true"]');
        if (activeElement) {
          activeElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      }, 100);
    }
  }, [loadModalVisible, currentTemplateName]);
  // Sync favorite template index whenever templates change (independent of initialization)
  useEffect(() => {
    if (templates.length > 0) {
      const favoriteTemplate = templates.find((t) => t.isDefault === true);
      if (favoriteTemplate) {
        setFavoriteTemplateIndex(templates.indexOf(favoriteTemplate));
      } else {
        setFavoriteTemplateIndex(null);
      }
    } else {
      // Clear favorite index when templates are deleted/emptied
      setFavoriteTemplateIndex(null);
    }
  }, [templates]);

  useEffect(() => {
    if (mlState.errorType === ErrorTypes.InvalidData && mlState.warnDetails) {
      setFeaturesWithError(mlState.warnDetails?.featuresWithNaOrZero ?? []);
    } else if (mlState.errorType === ErrorTypes.SingularValue && mlState.warnDetails) {
      setFeaturesWithError(
        mlState.warnDetails?.featuresWithSingleValue
          ? Object.keys(mlState.warnDetails.featuresWithSingleValue)
          : []
      );
    } else {
      setFeaturesWithError([]);
    }
  }, [mlState.errorType, mlState.errorMessage, mlState.warnDetails]);

  const getMonotonicPopoverContent = (item: MlFeature) => (
    <MonotonicPopoverContent>
      <div>Monotonicity</div>
      <Space direction="horizontal">
        <Button
          size="small"
          type={item.Monotonic === 1 ? "primary" : "default"}
          onClick={() => {
            const updatedList = featureList.map((f) =>
              f.Property === item.Property ? { ...f, Monotonic: 1 } : f
            );
            setFeatureList(updatedList);
            dispatch(setInputFeatures(updatedList));
            setMonotonicPopover(null);
          }}>
          <MonotonicityButtonContent color={item.Monotonic === 1 ? "white" : "#52c41a"}>
            ↑
          </MonotonicityButtonContent>
          Increasing
        </Button>
        <Button
          size="small"
          type={item.Monotonic === -1 ? "primary" : "default"}
          onClick={() => {
            const updatedList = featureList.map((f) =>
              f.Property === item.Property ? { ...f, Monotonic: -1 } : f
            );
            setFeatureList(updatedList);
            dispatch(setInputFeatures(updatedList));
            setMonotonicPopover(null);
          }}>
          <MonotonicityButtonContent color={item.Monotonic === -1 ? "white" : "#f5222d"}>
            ↓
          </MonotonicityButtonContent>
          Decreasing
        </Button>
        <Button
          size="small"
          type={item.Monotonic === 0 ? "primary" : "default"}
          onClick={() => {
            const updatedList = featureList.map((f) =>
              f.Property === item.Property ? { ...f, Monotonic: 0 } : f
            );
            setFeatureList(updatedList);
            dispatch(setInputFeatures(updatedList));
            setMonotonicPopover(null);
          }}>
          <MonotonicityButtonContent color={item.Monotonic === 0 ? "white" : "#bfbfbf"}>
            -
          </MonotonicityButtonContent>
          None
        </Button>
      </Space>
    </MonotonicPopoverContent>
  );

  const handleAddFeature = (value: IGroupBy) => {
    if (featureList.find((f) => f.Property === value.property)) {
      toast.warning("Feature already added.");
      return;
    }
    let niceName = value.title;
    if (value.units) {
      const unitMatches = value.title.match(/\([^)]+\)/g);
      if (unitMatches) {
        // Check if any matched unit exists in value.units
        unitMatches.forEach((unitMatch) => {
          const unitText = unitMatch.replace(/[()]/g, "");
          const hasMatchingUnit = Object.values(value.units).some(
            (unitValue) => unitValue === unitText
          );
          if (hasMatchingUnit) {
            // Strip unit from niceName as units should be dynamic
            niceName = niceName.replace(unitMatch, "").trim();
          }
        });
      }
    }
    const newFeature: MlFeature = {
      Property: value.property,
      NiceName: niceName,
      CanBeZero: true,
      category: value.group,
      checked: false,
      Monotonic: 0,
      product: value.product,
      unit: value.units,
      dataType: value.dataType
    };

    const updatedFeatures = [...featureList, newFeature];
    setFeatureList(updatedFeatures);
    dispatch(setInputFeatures(updatedFeatures));
  };

  const handleDeleteFeature = (property: string) => {
    const updated = featureList.filter((f) => f.Property !== property);
    setFeatureList(updated);
    dispatch(setInputFeatures(updated));
  };

  const startEditingTemplate = (index: number) => {
    setEditingTemplateIndex(index);
  };

  const cancelEditingTemplate = () => {
    setEditingTemplateIndex(null);
  };

  const handleTemplateRenamed = async (updatedTemplates: StoredFeatureTemplate[]) => {
    await saveTemplates(updatedTemplates);
    // Find the renamed template and update currentTemplateName
    if (editingTemplateIndex !== null) {
      const renamedTemplate = updatedTemplates[editingTemplateIndex];
      if (renamedTemplate) {
        setCurrentTemplateName(renamedTemplate.name);
      }
    }
    setEditingTemplateIndex(null);
  };

  const handleDeleteTemplate = async (index: number, name: string) => {
    const updatedTemplates = templates.filter((_, i) => i !== index);
    await saveTemplates(updatedTemplates);
    toast.success(`Template "${name}" deleted`);
  };

  const handleLoadTemplate = () => {
    setLoadModalVisible(true);
  };

  const renderLoadTemplateModal = () => {
    // Always include DEFAULT_TEMPLATE at the beginning
    const allTemplates = [DEFAULT_TEMPLATE, ...templates];

    return (
      <TemplateModal
        open={loadModalVisible}
        onCancel={() => setLoadModalVisible(false)}
        title="Load Template"
        width={600}
        footer={null}>
        <List
          size="small"
          dataSource={allTemplates}
          locale={{ emptyText: "No saved templates" }}
          renderItem={(template, index) => {
            const isActive = template.name === currentTemplateName;
            const templateStorageIndex = index - 1;
            const isFavorite = favoriteTemplateIndex === templateStorageIndex;

            return (
              <TemplateListItem
                data-template-active={isActive}
                $isActive={isActive}
                onClick={() => {
                  // Restore all template state: features, normalization, and target
                  setFeatureList(template.features);
                  dispatch(setInputFeatures(template.features));
                  dispatch(
                    setMLNormalizationFeatures(template.normalizationFeatures ?? [])
                  );
                  // Fall back to DEFAULT_TARGET for templates saved before target was persisted
                  if (template.targetFeature) {
                    dispatch(setTargetFeature(template.targetFeature));
                  } else {
                    dispatch(setTargetFeature(DEFAULT_TARGET));
                  }
                  setCurrentTemplateName(template.name);
                  setLoadModalVisible(false);
                  dispatch(clearError());
                  dispatch(clearSummary());
                  toast.success(`Loaded template "${template.name}"`);
                }}>
                <div style={{ flex: 1 }}>
                  <TemplateHeader>
                    {editingTemplateIndex === templateStorageIndex && index !== 0 ? (
                      <InlineTemplateRename
                        currentName={template.name}
                        templateIndex={templateStorageIndex}
                        templates={templates}
                        onSave={handleTemplateRenamed}
                        onCancel={cancelEditingTemplate}
                      />
                    ) : (
                      <>
                        <TemplateNameWithTooltip name={template.name} />

                        {/* Show rename/delete actions for all templates except DEFAULT_TEMPLATE (index 0) */}
                        {index !== 0 && (
                          <Space size="small">
                            <Tooltip title="Set as favourite">
                              <FavoriteIconButton
                                $isFavorite={isFavorite}
                                onClick={(e) => {
                                  e.stopPropagation();

                                  const updatedTemplates = templates.map((t, i) => ({
                                    ...t,
                                    isDefault: isFavorite
                                      ? false
                                      : i === templateStorageIndex
                                  }));

                                  setFavoriteTemplateIndex(
                                    isFavorite ? null : templateStorageIndex
                                  );
                                  saveTemplates(updatedTemplates);
                                }}>
                                <Icon
                                  path={isFavorite ? mdiStar : mdiStarOutline}
                                  size={"18px"}
                                />
                              </FavoriteIconButton>
                            </Tooltip>
                            <Tooltip title="Rename">
                              <IconButton
                                type="text"
                                size="small"
                                icon={<Icon path={mdiPencil} size={"18px"} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Adjust index to account for DEFAULT_TEMPLATE being at position 0
                                  startEditingTemplate(templateStorageIndex);
                                }}
                                className="template-actions"
                              />
                            </Tooltip>
                            <Popconfirm
                              title={`Are you sure you want to delete "${template.name}"?`}
                              okText="Delete"
                              cancelText="Cancel"
                              placement="bottom"
                              okButtonProps={{ danger: true }}
                              disabled={isActive}
                              onConfirm={(e) => {
                                e?.stopPropagation();
                                handleDeleteTemplate(templateStorageIndex, template.name);
                              }}
                              onCancel={(e) => e?.stopPropagation()}>
                              <Tooltip
                                title={
                                  isActive ? "Cannot delete active template" : "Delete"
                                }>
                                <IconButton
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<Icon path={mdiDelete} size={"18px"} />}
                                  disabled={isActive}
                                  onClick={(e) => e.stopPropagation()}
                                  className="template-actions"
                                />
                              </Tooltip>
                            </Popconfirm>
                          </Space>
                        )}
                      </>
                    )}
                  </TemplateHeader>

                  <TemplateInfo>
                    {template.features?.length || 0} feature
                    {template.features?.length !== 1 ? "s" : ""}
                    {index !== 0 &&
                      ` • ${new Date(template.createdAt).toLocaleDateString()}`}
                  </TemplateInfo>

                  {template.targetFeature && (
                    <TargetInfoContainer>
                      <TargetInfoLabel>
                        Target:{" "}
                        {template.targetFeature.AllUnits?.[unitType] &&
                        !template.targetFeature.NiceName.endsWith(
                          `(${template.targetFeature.AllUnits[unitType]})`
                        )
                          ? `${template.targetFeature.NiceName} (${template.targetFeature.AllUnits[unitType]})`
                          : template.targetFeature.NiceName}
                      </TargetInfoLabel>
                      {(() => {
                        const targetNorm = template.normalizationFeatures?.find(
                          (n) => n.ApplyTo.Property === template.targetFeature?.Property
                        );
                        if (!targetNorm?.NormalizeBy) return null;
                        const nb = targetNorm.NormalizeBy;
                        const normUnit = nb.unit?.[unitType] ?? nb.Unit;
                        const parts = [nb.NiceName];
                        if (nb.Value != null) parts.push(`${nb.Value}`);
                        if (normUnit) parts.push(normUnit);
                        return (
                          <TargetNormLabel>
                            Normalized by: {parts.join(" ")}
                          </TargetNormLabel>
                        );
                      })()}
                    </TargetInfoContainer>
                  )}

                  <CollapseContainer onClick={(e) => e.stopPropagation()}>
                    <Collapse
                      ghost
                      expandIcon={({ isActive }) => (
                        <div
                          className={`collapseChevron ${isActive ? "active" : ""}`}></div>
                      )}>
                      <Collapse.Panel key="features" header="View features">
                        <TemplateFeaturesTable
                          features={template.features || []}
                          normalizationFeatures={template.normalizationFeatures}
                          unitType={unitType}
                          categoryList={categoryList}
                        />
                      </Collapse.Panel>
                    </Collapse>
                  </CollapseContainer>
                </div>
              </TemplateListItem>
            );
          }}
        />
      </TemplateModal>
    );
  };

  const getFeatureDisplayName = (item: MlFeature) => {
    // Use getColumnLabelWithContext for consistent formatting
    const col = getGroupByListColumnByProperty(
      categoryList,
      formatPropertyName(item.Property)
    );

    if (!col) {
      // Fallback to original logic if column not found
      const unitMatch = item.NiceName.match(/\(([^)]+)\)/);
      let displayName = item.NiceName;
      const isPerformanceFeature = item.category === "Performance";
      if (unitMatch && !isPerformanceFeature) {
        try {
          const convertedUnit = getDisplayUnit(unitMatch[0]);
          if (convertedUnit && convertedUnit !== unitMatch[0]) {
            displayName = item.NiceName.replace(unitMatch[0], convertedUnit);
          }
        } catch (error) {
          // getTargetUnit will fail if imperial unit is passed in with imperial unitType
        }
      } else {
        const resolvedUnit = item.unit?.[unitType];
        if (resolvedUnit && !displayName.includes(`(${resolvedUnit})`)) {
          displayName = `${item.NiceName} (${resolvedUnit})`;
        }
      }
      return item.product
        ? `${getProductDisplayName(item.product)}: ${displayName}`
        : displayName;
    }

    // Global normalization doesn't apply in ML context
    const contextTitle = getColumnLabelWithContext(col, false, undefined);

    return contextTitle.fullContextTitle || item.NiceName;
  };

  const resolveFeatureDataType = (item: MlFeature) =>
    item.dataType ??
    getGroupByListColumnByProperty(categoryList, formatPropertyName(item.Property))
      ?.dataType;

  const handleResetToDefault = () => {
    const favorite =
      favoriteTemplateIndex != null ? templates[favoriteTemplateIndex] : undefined;
    const templateToReset = favorite ?? DEFAULT_TEMPLATE;
    const hasCustomDefault = favorite != null;

    Modal.confirm({
      title: "Reset to Default",
      content: hasCustomDefault
        ? `Are you sure you want to reset to your favourite template "${templateToReset.name}"?`
        : "Are you sure you want to reset to the default list of features?",
      okText: "Reset",
      cancelText: "Cancel",
      onOk: () => {
        // Reset to favorite template if set, otherwise to default
        setFeatureList(templateToReset.features);
        dispatch(setInputFeatures(templateToReset.features));
        dispatch(setMLNormalizationFeatures(templateToReset.normalizationFeatures ?? []));
        if (templateToReset.targetFeature) {
          dispatch(setTargetFeature(templateToReset.targetFeature));
        } else {
          dispatch(setTargetFeature(DEFAULT_TARGET));
        }
        setCurrentTemplateName(templateToReset.name);
        dispatch(clearError());
        dispatch(clearSummary());
        toast.success(`Reset to ${hasCustomDefault ? "favourite" : "default"} features`);
      }
    });
  };

  const handleNormalizeSettingsChange = (feature: MlFeature, next) => {
    const normalizeByFeature: NormalizeFeaturePackage["NormalizeBy"] | null =
      next?.useNormalizeBy
        ? {
            Property: next.field || DEFAULT_NORMALIZE_BY.field,
            NiceName: next.displayName || next.field || DEFAULT_NORMALIZE_BY.field,
            CanBeZero: true,
            Monotonic: 0,
            checked: true,
            category: "",
            unit: next.units,
            Value: next.per,
            Unit: next.unit
          }
        : null;

    dispatch(
      setMLNormalizationFeatureSetting({
        applyTo: feature,
        normalizeBy: normalizeByFeature
      })
    );
  };

  const getNormalizationSettingByProperty = (property: string): INormalizeBySetting => {
    const normalizationItems =
      (mlState?.normalizationFeatures as NormalizeFeaturePackage[]) ?? [];

    const matched = normalizationItems.find(
      (item) => item?.ApplyTo?.Property === property
    );

    if (!matched?.NormalizeBy) {
      return {
        ...DEFAULT_NORMALIZE_BY,
        useNormalizeBy: false
      };
    }

    return {
      ...DEFAULT_NORMALIZE_BY,
      field: matched.NormalizeBy.Property,
      displayName: matched.NormalizeBy.NiceName,
      unit:
        matched.NormalizeBy.Unit ??
        matched.NormalizeBy?.unit?.[unitType] ??
        DEFAULT_NORMALIZE_BY.unit,
      units: matched.NormalizeBy?.unit ?? undefined,
      per: matched.NormalizeBy.Value ?? DEFAULT_NORMALIZE_BY.per,
      useNormalizeBy: true
    };
  };

  return (
    <RootContainer>
      {renderLoadTemplateModal()}
      <StyledCard>
        <HeaderContainer>
          <FeatureSelectorRow>
            <h5>Input Features</h5>
            <Space style={{ marginBottom: 8 }}>
              <Tooltip title={isLoading ? "Loading templates..." : "Reset to default"}>
                <IconButton
                  className="resetToDefaultButton"
                  icon={<Icon path={mdiRefresh} size={"18px"} />}
                  onClick={handleResetToDefault}
                  disabled={!defaultFeatures.length || isLoading}
                />
              </Tooltip>

              <SaveTemplateButton
                featureList={featureList}
                templates={templates}
                normalizationFeatures={
                  (mlState?.normalizationFeatures as NormalizeFeaturePackage[]) ?? []
                }
                targetFeature={(mlState?.targetFeature as MlTarget) ?? undefined}
                onTemplateSaved={async (name: string) => {
                  // Persist features, normalization, and target together as a template
                  const newTemplate: StoredFeatureTemplate = {
                    name,
                    features: featureList,
                    createdAt: new Date().toISOString(),
                    normalizationFeatures:
                      (mlState?.normalizationFeatures as NormalizeFeaturePackage[]) ?? [],
                    targetFeature: (mlState?.targetFeature as MlTarget) ?? undefined
                  };
                  const updatedTemplates = [...templates, newTemplate];
                  await saveTemplates(updatedTemplates);
                  setCurrentTemplateName(name);
                  toast.success(`Template "${name}" saved!`);
                }}
              />

              <Tooltip title="Load template">
                <IconButton
                  icon={<Icon path={mdiFolderOpen} size={"18px"} />}
                  onClick={handleLoadTemplate}
                  disabled={!templates.length}
                />
              </Tooltip>
            </Space>
          </FeatureSelectorRow>

          <GroupBy
            value={{ property: "Header.EntityName", title: "Entity Name" }}
            dataTypeFilters={["Number", "Integer", "Text"]}
            hideDateRange={true}
            onChange={(value) => handleAddFeature(value)}>
            <StyledAddFeatureButton icon={<Icon path={mdiPlus} size={1} />}>
              Feature
            </StyledAddFeatureButton>
          </GroupBy>
        </HeaderContainer>

        <ScrollableList
          dataSource={featureList}
          renderItem={(item) => {
            const displayName = getFeatureDisplayName(item);
            const resolvedDataType = resolveFeatureDataType(item);
            const normalizeByFeature =
              featuresWithAppliedNormalization.find(
                (feat) => feat.ApplyTo?.Property === item.Property
              )?.NormalizeBy ?? null;
            const normalizedByLabel = getNormalizedByLabel(
              normalizeByFeature,
              unitType,
              categoryList
            );
            return (
              <ListItemWrapper
                actions={[
                  resolvedDataType === DataEnum.Integer ||
                  resolvedDataType === DataEnum.Number ? (
                    <NormalizeByToggle
                      key="Normalize"
                      value={getNormalizationSettingByProperty(item.Property)}
                      onChange={(next) => handleNormalizeSettingsChange(item, next)}
                      testIdPrefix={`mlNormalizeToggle-${item.Property}`}
                    />
                  ) : null,
                  <Popover
                    key="monotonic"
                    content={getMonotonicPopoverContent(item)}
                    trigger="click"
                    open={monotonicPopover === item.Property}
                    onOpenChange={(visible) =>
                      setMonotonicPopover(visible ? item.Property : null)
                    }>
                    <IconButton
                      type="text"
                      icon={
                        item.Monotonic === 1 ? (
                          <MonotonicityButtonContent color="#52c41a">
                            ↑
                          </MonotonicityButtonContent>
                        ) : item.Monotonic === -1 ? (
                          <MonotonicityButtonContent color="#f5222d">
                            ↓
                          </MonotonicityButtonContent>
                        ) : (
                          <MonotonicityButtonContent color="#bfbfbf">
                            -
                          </MonotonicityButtonContent>
                        )
                      }
                    />
                  </Popover>,

                  <IconButton
                    key={"delete"}
                    onClick={() => handleDeleteFeature(item.Property)}
                    type="text"
                    danger
                    className="featureSelectorDeleteButton"
                    icon={<Icon path={mdiDelete} size={"18px"} />}
                  />
                ]}>
                <FeatureRow>
                  <Tooltip
                    title={
                      normalizedByLabel
                        ? `${displayName} ${normalizedByLabel}`
                        : displayName
                    }
                    placement="left">
                    <FeatureNameColumn>
                      <FeatureName>{displayName}</FeatureName>
                      {normalizedByLabel ? (
                        <NormalizedByLabel>{normalizedByLabel}</NormalizedByLabel>
                      ) : null}
                    </FeatureNameColumn>
                  </Tooltip>
                  {featuresWithError && featuresWithError.includes(item.Property) ? (
                    <Tooltip
                      title={
                        "Feature contains invalid well data. Click to view details."
                      }>
                      <IconButton
                        type="text"
                        danger
                        onClick={() => setErrorModalVisible(true)}
                        icon={
                          <Icon path={mdiAlertCircle} size={"16px"} color="#f0ad4e" />
                        }
                      />
                    </Tooltip>
                  ) : null}
                </FeatureRow>
              </ListItemWrapper>
            );
          }}
          locale={{ emptyText: "No input features selected" }}
        />
      </StyledCard>
    </RootContainer>
  );
};

export default memo(FeatureSelector);
